import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { CPR_CHART_COLORS } from 'config/index';
import { FFR_SHARED_MARGIN } from './FFRChart';
import { getDisplayRuntimeProfile } from 'config/displayRuntime';

export interface DataPoint {
  x: number;
  y: number;
}

export interface SelectedIndices {
  proximal: number;
  mid: number;
  distal: number;
}

type MarkerKey = 'proximal' | 'mid' | 'distal' | null;

export interface ReferenceMarkerConfig {
  idx: number;
  markerKey?: Exclude<MarkerKey, null>;
  dashed?: boolean;
  interactive?: boolean;
  color?: string;
}

type Margin = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export interface LineConfig {
  data: DataPoint[];
  y: (d: DataPoint) => number;
  yDomain: [number, number];
  yLabel: string;
  color: string;
  strokeWidth?: number;
}

interface DualLineChartProps {
  lineA: LineConfig;
  lineB: LineConfig;
  x: (d: DataPoint) => number;
  width: number;
  height: number;
  title?: string;
  selectedIndices: SelectedIndices;
  dragNodeId?: number;
  draggingKey?: MarkerKey;
  onMarkerMouseDown?: (key: MarkerKey) => void;
  onDragStart?: (e: PointerEvent, idx: number) => void;
  onDrag?: (e: PointerEvent, idx: number) => void;
  onDragEnd?: () => void;
  sharedMargin?: Margin;
  interactiveMarkers?: boolean;
  windowSize?: number;
  uiScale?: number;
  onTransientIndexStart?: (idx: number) => void;
  onTransientIndexChange?: (idx: number) => void;
  onTransientIndexEnd?: () => void;
  referenceMarkers?: ReferenceMarkerConfig[];
}

function movingAverage(arr: ArrayLike<number>, window: number) {
  if (window <= 1) return Array.from(arr);

  const out: number[] = [];
  const n = arr.length;
  const half = Math.floor(window / 2);

  for (let i = 0; i < n; ++i) {
    let sum = 0;
    let cnt = 0;
    for (let j = i - half; j <= i + half; ++j) {
      if (j >= 0 && j < n) {
        sum += arr[j];
        cnt++;
      }
    }
    out.push(sum / cnt);
  }

  return out;
}

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

export default function DualLineChart({
  lineA,
  lineB,
  x,
  width,
  height,
  title = 'Diameter & Area Graph',
  selectedIndices,
  dragNodeId = -1,
  draggingKey = null,
  onMarkerMouseDown,
  onDragStart,
  onDrag,
  onDragEnd,
  sharedMargin,
  interactiveMarkers = true,
  windowSize = 10,
  uiScale = 1,
  onTransientIndexStart,
  onTransientIndexChange,
  onTransientIndexEnd,
  referenceMarkers,
}: DualLineChartProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const [, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const lastDragIndexRef = useRef<number | null>(null);
  const ffrDraggingRef = useRef(false);
  const callbacksRef = useRef({
    onMarkerMouseDown,
    onDragStart,
    onDrag,
    onDragEnd,
  });

  useEffect(() => {
    callbacksRef.current = {
      onMarkerMouseDown,
      onDragStart,
      onDrag,
      onDragEnd,
    };
  });

  const smoothedLineAData = useMemo(() => {
    if (!lineA.data.length) return [];
    const yArr = lineA.data.map(lineA.y);
    const smoothedY = movingAverage(yArr, windowSize);
    return lineA.data.map((d, i) => ({ ...d, ySmoothed: smoothedY[i] }));
  }, [lineA.data, lineA.y, windowSize]);

  const smoothedLineBData = useMemo(() => {
    if (!lineB.data.length) return [];
    const yArr = lineB.data.map(lineB.y);
    const smoothedY = movingAverage(yArr, windowSize);
    return lineB.data.map((d, i) => ({ ...d, ySmoothed: smoothedY[i] }));
  }, [lineB.data, lineB.y, windowSize]);

  const fontScale = useMemo(() => {
    if (!width || !height) return 1;
    const wScale = width / 720;
    const hScale = height / 280;
    const typographyScale = getDisplayRuntimeProfile().typographyScale;
    return Math.max(0.7, Math.min(1.22, Math.min(wScale, hScale) * typographyScale));
  }, [width, height]);

  const axisFontSize = Math.max(9, Math.round(12 * fontScale));
  const titleFontSize = Math.max(11, Math.round(13 * fontScale));
  const labelFontSize = Math.max(10, Math.round(12 * fontScale));

  const adaptiveMargin = useMemo(
    () => ({
      top: Math.max(14, Math.round(labelFontSize * 1.6)),
      right: Math.max(30, Math.round(axisFontSize * 3.5)),
      bottom: Math.max(40, Math.round((axisFontSize + labelFontSize) * 2.2)),
      left: Math.max(46, Math.round(labelFontSize * 3.8)),
    }),
    [axisFontSize, labelFontSize],
  );

  const margin = sharedMargin ?? adaptiveMargin;

  const innerWidth = useMemo(
    () => Math.max(0, width - margin.left - margin.right),
    [width, margin.left, margin.right],
  );

  const innerHeight = useMemo(
    () => Math.max(0, height - margin.top - margin.bottom),
    [height, margin.top, margin.bottom],
  );

  const xExtent = useMemo<[number, number]>(() => {
    if (!lineB.data.length) return [0, 100];
    const e = d3.extent(lineB.data, x);
    const min = Number(e[0]);
    const max = Number(e[1]);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      return [0, 100];
    }
    return [min, max];
  }, [lineB.data, x]);

  const xScale = useMemo(
    () => d3.scaleLinear().domain(xExtent).range([0, innerWidth]),
    [xExtent, innerWidth],
  );

  const yScaleL = useMemo(
    () => d3.scaleLinear().domain(lineB.yDomain).range([innerHeight, 0]),
    [lineB.yDomain, innerHeight],
  );

  const yScaleR = useMemo(
    () => d3.scaleLinear().domain(lineA.yDomain).range([innerHeight, 0]),
    [lineA.yDomain, innerHeight],
  );

  const xAxis = useMemo(
    () =>
      d3
        .axisBottom(xScale)
        .ticks(Math.max(2, Math.floor(width / 120)))
        .tickSizeOuter(0),
    [xScale, width],
  );

  const yAxisL = useMemo(
    () => d3.axisLeft(yScaleL).ticks(Math.floor(innerHeight / 30)),
    [yScaleL, innerHeight],
  );

  const yAxisR = useMemo(
    () => d3.axisRight(yScaleR).ticks(Math.floor(innerHeight / 30)),
    [yScaleR, innerHeight],
  );

  function drawLabel(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    xPx: number,
    yPx: number,
    color: string,
    text: string,
    prefer: 'start' | 'middle' | 'end' = 'end',
  ) {
    const M = 8;
    let xPos = clamp(xPx, M, innerWidth - M);

    let anchor: 'start' | 'middle' | 'end' = prefer;
    if (xPos <= M + 10) anchor = 'start';
    else anchor = 'end';

    const sel = g
      .append('text')
      .attr('class', 'one-line')
      .attr('x', xPos)
      .attr('y', yPx)
      .attr('text-anchor', anchor)
      .attr('dominant-baseline', 'middle')
      .attr('fill', color)
      .attr('font-size', `${Math.max(10, Math.round(14 * fontScale))}px`)
      .attr('font-weight', 'bold')
      .text(text);

    const bbox = (sel.node() as SVGTextElement).getBBox();
    if (bbox.x < 0) {
      sel.attr('text-anchor', 'start').attr('x', M);
    } else if (bbox.x + bbox.width > innerWidth) {
      sel.attr('text-anchor', 'end').attr('x', innerWidth - M);
    }
  }

  useEffect(() => {
    if (!interactiveMarkers) return;

    const bottomSafe = Math.max(4, Math.round(labelFontSize * 0.4));

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr(
        'style',
        'max-width:100%; height:auto; height:intrinsic; background-color:#0D0D0D;',
      );

    svg.selectAll('*').remove();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    gRef.current = g.node();

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('fill', 'white')
      .attr('font-size', `${axisFontSize}px`);

    g.append('g')
      .call(yAxisL)
      .call((gg) =>
        gg
          .selectAll('text')
          .attr('fill', lineB.color)
          .attr('font-size', `${axisFontSize}px`),
      );

    g.append('g')
      .attr('transform', `translate(${innerWidth},0)`)
      .call(yAxisR)
      .call((gg) =>
        gg
          .selectAll('text')
          .attr('fill', lineA.color)
          .attr('font-size', `${axisFontSize}px`),
      );

    g.append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);

    g.append('line')
      .attr('x1', innerWidth)
      .attr('x2', innerWidth)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);

    const lineAPath = d3
      .line<DataPoint & { ySmoothed: number }>()
      .x((d) => xScale(x(d)))
      .y((d) => yScaleR(d.ySmoothed));

    const lineBPath = d3
      .line<DataPoint & { ySmoothed: number }>()
      .x((d) => xScale(x(d)))
      .y((d) => yScaleL(d.ySmoothed));

    if (smoothedLineAData.length) {
      g.append('path')
        .datum(smoothedLineAData)
        .attr('fill', 'none')
        .attr('stroke', lineA.color)
        .attr('stroke-width', lineA.strokeWidth ?? 2)
        .attr('d', lineAPath);
    }

    if (smoothedLineBData.length) {
      g.append('path')
        .datum(smoothedLineBData)
        .attr('fill', 'none')
        .attr('stroke', lineB.color)
        .attr('stroke-width', lineB.strokeWidth ?? 2)
        .attr('d', lineBPath);
    }

    if (title) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', Math.max(12, Math.round(14 * fontScale)))
        .attr('text-anchor', 'middle')
        .attr('fill', '#FFE066')
        .attr('font-size', `${titleFontSize}px`)
        .attr('font-weight', 'bold')
        .text(title);
    }

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', height - bottomSafe)
      .attr('dominant-baseline', 'text-after-edge')
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', `${labelFontSize}px`)
      .text('Length (mm)');

    const yLabelOffsetA = Math.max(10, Math.round(labelFontSize * 1.2));
    const yLabelOffsetB = Math.max(22, Math.round(labelFontSize * 2.0));

    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', yLabelOffsetB)
      .attr('fill', lineA.color)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${labelFontSize}px`)
      .text(lineA.yLabel);

    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', yLabelOffsetA)
      .attr('fill', lineB.color)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${labelFontSize}px`)
      .text(lineB.yLabel);

    const getNearestDataIndex = (targetX: number) => {
      const data = lineB.data;
      const lastIndex = data.length - 1;
      if (lastIndex < 0) return -1;
      if (lastIndex === 0) return 0;

      const firstX = x(data[0]);
      const lastX = x(data[lastIndex]);
      if (!Number.isFinite(firstX) || !Number.isFinite(lastX)) {
        let nearest = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        for (let i = 0; i <= lastIndex; i += 1) {
          const distance = Math.abs(x(data[i]) - targetX);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = i;
          }
        }
        return nearest;
      }

      const ascending = firstX <= lastX;
      if (ascending) {
        if (targetX <= firstX) return 0;
        if (targetX >= lastX) return lastIndex;
      } else {
        if (targetX >= firstX) return 0;
        if (targetX <= lastX) return lastIndex;
      }

      let lo = 0;
      let hi = lastIndex;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midX = x(data[mid]);
        if ((ascending && midX < targetX) || (!ascending && midX > targetX)) {
          lo = mid + 1;
        } else {
          hi = mid;
        }
      }

      const prev = Math.max(0, lo - 1);
      return Math.abs(x(data[prev]) - targetX) <= Math.abs(x(data[lo]) - targetX)
        ? prev
        : lo;
    };

    svg.on('mousedown', (event: any) => {
      event.preventDefault();
      draggingRef.current = true;
      setDragging(true);
      callbacksRef.current.onMarkerMouseDown?.(null);

      const [mouseX] = d3.pointer(event, g.node());
      const idx = getNearestDataIndex(xScale.invert(mouseX));

      if (idx >= 0) {
        lastDragIndexRef.current = idx;
        callbacksRef.current.onDragStart?.(event, idx);
      }
    });

    svg.on('mousemove', (event: any) => {
      if (!draggingRef.current) return;
      event.preventDefault();

      const [mouseX] = d3.pointer(event, g.node());
      const idx = getNearestDataIndex(xScale.invert(mouseX));

      if (idx >= 0 && idx !== lastDragIndexRef.current) {
        lastDragIndexRef.current = idx;
        callbacksRef.current.onDrag?.(event, idx);
      }
    });

    svg.on('mouseup', (event: any) => {
      event.preventDefault();
      draggingRef.current = false;
      lastDragIndexRef.current = null;
      setDragging(false);
      callbacksRef.current.onDragEnd?.();
    });


    return () => {
      svg.on('mousedown', null);
      svg.on('mousemove', null);
      svg.on('mouseup', null);
    };
  }, [
    interactiveMarkers,
    width,
    height,
    innerWidth,
    innerHeight,
    margin.left,
    margin.top,
    lineA.color,
    lineA.strokeWidth,
    lineA.yLabel,
    lineB.color,
    lineB.data,
    lineB.strokeWidth,
    lineB.yLabel,
    smoothedLineAData,
    smoothedLineBData,
    xAxis,
    yAxisL,
    yAxisR,
    xScale,
    yScaleL,
    yScaleR,
    title,
    fontScale,
    axisFontSize,
    titleFontSize,
    labelFontSize,
    x,
  ]);

  const drawActiveLine = () => {
    if (!interactiveMarkers || !gRef.current) return;
    const gSel = d3.select(gRef.current);
    gSel.selectAll('.one-line').remove();

    if (dragNodeId === -1) return;

    const idxA = Math.min(
      dragNodeId,
      Math.max(0, smoothedLineAData.length - 1),
    );
    const idxB = Math.min(
      dragNodeId,
      Math.max(0, smoothedLineBData.length - 1),
    );

    const smdataA = smoothedLineAData[idxA];
    const smdataB = smoothedLineBData[idxB];
    if (!smdataA || !smdataB) return;

    const xPos = xScale(x(smdataA));
    const textY1 = Math.max(16, innerHeight * 0.18);
    const textY2 = textY1 + 20;
    const valB = lineB.y(smdataB);
    const valA = lineA.y(smdataA);
    const xLine = clamp(xPos, 0, innerWidth);

    if (draggingKey === null) {
      gSel
        .append('line')
        .attr('class', 'one-line')
        .attr('x1', xLine)
        .attr('x2', xLine)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#33FF33')
        .attr('stroke-width', 2);
    }

    drawLabel(
      gSel,
      xPos - 6,
      textY1,
      lineB.color,
      `${Number.isInteger(valB) ? valB : valB.toFixed(2)} mm`,
      'end',
    );

    drawLabel(
      gSel,
      xPos - 6,
      textY2,
      lineA.color,
      `${Number.isInteger(valA) ? valA : valA.toFixed(2)} mm²`,
      'end',
    );
  };

  useEffect(() => {
    if (!interactiveMarkers) return;
    if (!selectedIndices) return;
    if (!gRef.current) return;

    const g = d3.select(gRef.current);
    g.selectAll('.marker-group').remove();

    const strokeWidth = 2;

    const markers = referenceMarkers?.length
      ? referenceMarkers
      : [
          { markerKey: 'proximal' as const, idx: selectedIndices.proximal, interactive: true },
          { markerKey: 'mid' as const, idx: selectedIndices.mid, interactive: true },
          { markerKey: 'distal' as const, idx: selectedIndices.distal, interactive: true },
        ];

    markers.forEach((marker) => {
      const { markerKey, idx } = marker;
      if (idx < 0 || !lineB.data[idx]) return;
      const xPos = xScale(lineB.data[idx].x);
      const stroke = marker.color ?? (markerKey ? CPR_CHART_COLORS[markerKey] : '#9ca3af');
      const isInteractive = marker.interactive !== false && !!markerKey;

      const markerGroup = g.append('g')
        .attr('class','marker-group')
        .attr('data-key',markerKey||'')
      if (isInteractive) {
        markerGroup.append('line')
          .attr('class', 'three-line-hit')
          .attr('x1', xPos)
          .attr('x2', xPos)
          .attr('y1', yScaleL(lineB.yDomain[0]))
          .attr('y2', yScaleL(lineB.yDomain[1]))
          .attr('stroke', stroke)
          .attr('stroke-width', strokeWidth + 10)
          .attr('stroke-opacity', 0)
          .style('cursor', 'move')
          .on('mousedown', (ev: any) => {
            ev.preventDefault();
            ev.stopPropagation();
            draggingRef.current = true;
            lastDragIndexRef.current = idx;
            setDragging(true);
            callbacksRef.current.onMarkerMouseDown?.(markerKey);
            callbacksRef.current.onDragStart?.(ev, idx);
          });
      }

      const visualLine = markerGroup.append('line')
        .attr('class', 'three-line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', yScaleL(lineB.yDomain[0]))
        .attr('y2', yScaleL(lineB.yDomain[1]))
        .attr('stroke', stroke)
        .attr('stroke-width', marker.dashed ? Math.max(1, strokeWidth - 0.5) : strokeWidth)
        .attr('stroke-opacity', marker.dashed ? 0.85 : 1)
        .style('pointer-events', 'none');

      if (marker.dashed) {
        visualLine.attr('stroke-dasharray', '4 4');
      }
    });
  }, [
    interactiveMarkers,
    selectedIndices,
    lineB.data,
    lineB.yDomain,
    xScale,
    yScaleL,
    referenceMarkers,
  ]);
  useEffect(() => {
    drawActiveLine();
  }, [
    dragNodeId,
    draggingKey,
    interactiveMarkers,
    smoothedLineAData,
    smoothedLineBData,
    xScale,
    x,
    innerHeight,
    innerWidth,
    lineA.color,
    lineA.y,
    lineB.color,
    lineB.y,
    fontScale,
  ]);

  if (interactiveMarkers) {
    return <svg ref={svgRef} style={{ userSelect: 'none' }} />;
  }

  const ffrMargin = sharedMargin ?? FFR_SHARED_MARGIN;
  const ffrScale = clamp(uiScale, 1, 1.8);
  const ffrAxisFontSize = Math.round(10 * ffrScale);
  const ffrValueFontSize = Math.round(11 * ffrScale);
  const ffrTitleFontSize = Math.round(12 * ffrScale);
  const ffrSubValueFontSize = Math.round(10 * ffrScale);
  const ffrStrokeWidth = 1.5 * ffrScale;
  const ffrInnerWidth = Math.max(
    0,
    width - ffrMargin.left - ffrMargin.right,
  );
  const ffrInnerHeight = Math.max(
    0,
    height - ffrMargin.top - ffrMargin.bottom,
  );

  const ffrBaseData = lineB.data.length ? lineB.data : lineA.data;

  const ffrXDomain = useMemo<[number, number]>(() => {
    if (!ffrBaseData.length) return [0, 100];

    const extent = d3.extent(ffrBaseData, (d) => x(d)) as [number, number];
    if (
      !Number.isFinite(extent[0]) ||
      !Number.isFinite(extent[1]) ||
      extent[0] === extent[1]
    ) {
      return [0, 100];
    }

    return extent;
  }, [ffrBaseData, x]);

  const ffrXScale = useMemo(
    () => d3.scaleLinear().domain(ffrXDomain).range([0, ffrInnerWidth]),
    [ffrXDomain, ffrInnerWidth],
  );

  const ffrYScaleA = useMemo(
    () => d3.scaleLinear().domain(lineA.yDomain).range([ffrInnerHeight, 0]),
    [lineA.yDomain, ffrInnerHeight],
  );

  const ffrYScaleB = useMemo(
    () => d3.scaleLinear().domain(lineB.yDomain).range([ffrInnerHeight, 0]),
    [lineB.yDomain, ffrInnerHeight],
  );

  const ffrLinePathA = useMemo(() => {
    if (!lineA.data.length) return '';

    const generator = d3
      .line<DataPoint>()
      .x((d) => ffrXScale(x(d)))
      .y((d) => ffrYScaleA(lineA.y(d)));

    return generator(lineA.data) ?? '';
  }, [lineA, x, ffrXScale, ffrYScaleA]);

  const ffrLinePathB = useMemo(() => {
    if (!lineB.data.length) return '';

    const generator = d3
      .line<DataPoint>()
      .x((d) => ffrXScale(x(d)))
      .y((d) => ffrYScaleB(lineB.y(d)));

    return generator(lineB.data) ?? '';
  }, [lineB, x, ffrXScale, ffrYScaleB]);

  const getFfrMarkerX = (idx: number) => {
    if (idx < 0 || idx >= ffrBaseData.length) return null;
    return ffrXScale(x(ffrBaseData[idx]));
  };

  const selectedMarkerX = getFfrMarkerX(selectedIndices.mid);

  const selectedMidPointB =
    selectedIndices.mid >= 0 && selectedIndices.mid < lineB.data.length
      ? lineB.data[selectedIndices.mid]
      : null;

  const selectedMidPointA =
    selectedIndices.mid >= 0 && selectedIndices.mid < lineA.data.length
      ? lineA.data[selectedIndices.mid]
      : null;

  const fixedLabelY2 = Math.round(32 * ffrScale);
  const fixedLabelY1 = Math.round(14 * ffrScale);
  const getNearestFfrIndex = (clientX: number, svgRect: DOMRect) => {
    if (!ffrBaseData.length || ffrInnerWidth <= 0) return -1;

    const localX = clamp(
      clientX - svgRect.left - ffrMargin.left,
      0,
      ffrInnerWidth,
    );

    let nearestIndex = 0;
    let minDist = Infinity;
    for (let i = 0; i < ffrBaseData.length; i++) {
      const px = ffrXScale(x(ffrBaseData[i]));
      const dist = Math.abs(px - localX);
      if (dist < minDist) {
        minDist = dist;
        nearestIndex = i;
      }
    }

    return nearestIndex;
  };

  const emitFfrIndex = (
    e: React.PointerEvent<SVGRectElement>,
    cb?: (idx: number) => void,
  ) => {
    const svgRect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!svgRect) return;
    const idx = getNearestFfrIndex(e.clientX, svgRect);
    if (idx >= 0) cb?.(idx);
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        backgroundColor: '#0D0D0D',
        userSelect: 'none',
        touchAction: 'none',
        display: 'block',
      }}
    >
      <g transform={`translate(${ffrMargin.left},${ffrMargin.top})`}>
        {ffrYScaleB.ticks(5).map((t, i) => (
          <g key={`grid-${i}`}>
            <line
              x1={0}
              x2={ffrInnerWidth}
              y1={ffrYScaleB(t)}
              y2={ffrYScaleB(t)}
              stroke="#2a2f36"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <text
              x={-6}
              y={ffrYScaleB(t) + 3}
              fill="#ffffff"
              textAnchor="end"
              fontSize={ffrAxisFontSize}
            >
              {t.toFixed(1)}
            </text>
          </g>
        ))}

        <g transform={`translate(0,${ffrInnerHeight})`}>
          {ffrXScale.ticks(Math.max(2, Math.floor(width / 120))).map((t, i) => (
            <text
              key={`x-${i}`}
              x={ffrXScale(t)}
              y={Math.round(18 * ffrScale)}
              fill="#9ca3af"
              textAnchor="middle"
              fontSize={ffrAxisFontSize}
            >
              {Math.round(t)}
            </text>
          ))}
        </g>

        {ffrYScaleA.ticks(5).map((t, i) => (
          <text
            key={`right-y-${i}`}
            x={ffrInnerWidth + 6}
            y={ffrYScaleA(t) + 3}
            fill={lineA.color}
            textAnchor="start"
            fontSize={ffrAxisFontSize}
          >
            {t.toFixed(1)}
          </text>
        ))}

        {ffrLinePathA && (
          <path
            d={ffrLinePathA}
            fill="none"
            stroke={lineA.color}
            strokeWidth={(lineA.strokeWidth ?? 1.5) * ffrScale}
          />
        )}

        {ffrLinePathB && (
          <path
            d={ffrLinePathB}
            fill="none"
            stroke={lineB.color}
            strokeWidth={(lineB.strokeWidth ?? 1.5) * ffrScale}
          />
        )}

        {selectedMarkerX !== null && (
          <line
            x1={selectedMarkerX}
            x2={selectedMarkerX}
            y1={0}
            y2={ffrInnerHeight}
            stroke="#33FF33"
            strokeWidth={ffrStrokeWidth}
          />
        )}

        {selectedMidPointB && selectedMarkerX !== null && (
          <text
            x={selectedMarkerX + Math.round(6 * ffrScale)}
            y={fixedLabelY1}
            textAnchor="start"
            fill="#ffffff"
            fontSize={ffrValueFontSize}
            fontWeight={700}
          >
            Diameter: {lineB.y(selectedMidPointB).toFixed(2)}
          </text>
        )}

        {selectedMidPointA && selectedMarkerX !== null && (
          <text
            x={selectedMarkerX + Math.round(6 * ffrScale)}
            y={fixedLabelY2}
            textAnchor="start"
            fill={lineA.color}
            fontSize={ffrSubValueFontSize}
            fontWeight={700}
          >
            Area: {lineA.y(selectedMidPointA).toFixed(2)}
          </text>
        )}

        <text
          x={-ffrMargin.left + 2}
          y={Math.round(-6 * ffrScale)}
          fill="#ffffff"
          textAnchor="start"
          fontSize={ffrTitleFontSize}
          fontWeight={700}
        >
          {lineB.yLabel}
        </text>

        <text
          x={ffrInnerWidth + ffrMargin.right - 2}
          y={Math.round(-6 * ffrScale)}
          fill={lineA.color}
          textAnchor="end"
          fontSize={ffrTitleFontSize}
          fontWeight={700}
        >
          {lineA.yLabel}
        </text>

        <text
          x={ffrInnerWidth / 2}
          y={ffrInnerHeight + ffrMargin.bottom - Math.round(4 * ffrScale)}
          fill="#ffffff"
          textAnchor="middle"
          fontSize={ffrValueFontSize}
        >
          Length (mm)
        </text>

        <rect
          x={0}
          y={0}
          width={ffrInnerWidth}
          height={ffrInnerHeight}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerDown={(e) => {
            ffrDraggingRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            emitFfrIndex(e, onTransientIndexStart);
          }}
          onPointerMove={(e) => {
            if (!ffrDraggingRef.current) return;
            emitFfrIndex(e, onTransientIndexChange);
          }}
          onPointerUp={(e) => {
            ffrDraggingRef.current = false;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            onTransientIndexEnd?.();
          }}
          onPointerCancel={(e) => {
            ffrDraggingRef.current = false;
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
            onTransientIndexEnd?.();
          }}
        />
      </g>

      <text
        x={width / 2}
        y={Math.round(14 * ffrScale)}
        textAnchor="middle"
        fill="#ffd400"
        fontSize={ffrTitleFontSize}
        fontWeight={700}
      >
        {title}
      </text>
    </svg>
  );
}
